import React, { useEffect, lazy, Suspense } from "react";
import { useDispatch } from "react-redux";
import initiateSocket from "./socket.js";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { BrowserRouter, Route, Switch } from "react-router-dom";
import Layout from "./HOC/Layout/Layout";

import ScrollTop from "./HOC/ScrollTop/ScrollTop";
import "react-dev-utils/webpackHotDevClient";

import ErrorBoundray from "./Components/ErrorBoundary";

import ProtectedRoute from "./Components/auth/ProtectedRoute";

import CreateAgreements from "./Components/NewDashboard/Agreements/CreateAgreements/CreateAgreements";
import EditAgreements from "./Components/NewDashboard/Agreements/EditAgreements/EditAgreement";

import CreateInvoice from "./Components/NewDashboard/invoices/CreateInvoice/CreateInvoice";
import EditInvoice from "./Components/NewDashboard/invoices/EditInvoice/EditInvoice";
import ResendInvoice from "./Components/NewDashboard/invoices/ResendInvoice/ResendInvoice.jsx";

// project related invoices
import CreateInvoiceInProject from "./Components/NewDashboard/projects/editProject/Invoices/CreateInvoice/CreateInvoice";
import EditInvoiceInProject from "./Components/NewDashboard/projects/editProject/Invoices/EditInvoice/EditInvoice";

// client realated invoices
import CreateInvoiceInClient from "./Components/NewDashboard/clients/editClient/Invoices/CreateInvoice/CreateInvoice.jsx";
import EditInvoiceInClient from "./Components/NewDashboard/clients/editClient/Invoices/EditInvoice/EditInvoice.jsx";

import LaunchToSetting from "./Components/NewDashboard/DB/LaunchToSetting";
import PublicViewAgreements from "./Components/NewDashboard/Agreements/PublicViewAgreements/ViewAgreements.jsx";
import ViewAgreements from "./Components/NewDashboard/Agreements/ViewAgreements/ViewAgreements.jsx";
import LaunchToTimers from "./Components/NewDashboard/DB/LaunchToTimers";
import LaunchToStatements from "./Components/NewDashboard/DB/LaunchToStatements.jsx";
import AOS from "aos";
import "aos/dist/aos.css";
import "./index.css";
import { useSelector } from "react-redux";
import LaunchToAdminDashboard from "./Components/NewDashboard/DB/LaunchToAdminDashboard.jsx";
import LaunchToUserInfo from "./Components/NewDashboard/DB/LaunchToUserInfo.jsx";
import LaunchToTasks from "./Components/NewDashboard/DB/LaunchToTasks.jsx";
import InvoicePublic from "./Components/NewDashboard/invoices/PublicViewInvoice/InvoicePublic.jsx";
import { addNotification } from "./store/storage/notificationSlice.js";

import LazyLoading from "./Components/UIs/LazyLoading.jsx";

import { refreshUserPlan } from "./store/storage/authSlice.js";
import { setIsPaymentProcessing } from "./store/storage/subscriptionSlice.js";
import LandingPage from "./Components/NewDashboard/LandingPage/LandingPage.jsx";

const HomePage = lazy(() => import("./Components/HomePage/HomePage.jsx"));
const TaskManagements = lazy(() => import("./Components/Features/TaskManagements/TaskManagements.jsx"));
const Invoicing = lazy(() => import("./Components/Features/Invoices/Invoicing.jsx"));
const Agreements = lazy(() => import("./Components/Features/Agreements/Agreements.jsx"));
const Transactions = lazy(() => import("./Components/Features/Transactions/Transactions.jsx"));
const Statements = lazy(() => import("./Components/Features/Statements/Statements.jsx"));
const TimeTracking = lazy(() => import ("./Components/Features/TimeTracking/TimeTracking.jsx"));
const ClientProjectManagement = lazy(() => import("./Components/Features/ClientProjectManagement/ClientProjectManagement.jsx"));

const Reviews = lazy(() => import("./Components/Reviews/Reviews.jsx"));
const WhyZodot = lazy(() => import("./Components/WhyZodot/WhyZodot.jsx"));
const TermsOfService = lazy(() => import("./Components/TermsOfService/TermsOfservice.jsx"));
const PrivacyPolicy = lazy(() => import("./Components/PrivacyPolicy/PrivacyPolicy.jsx"));
const ContactUs = lazy(() => import("./Components/ContactUs/ContactUs.jsx"));
const AboutUs = lazy(() => import("./Components/AboutUs/AboutUs.jsx"));
const PricePage = lazy(() => import("./Components/PricePage/PricePage.jsx"));
const Checkout = lazy(() => import("./Components/PricePage/Checkout.jsx"));

const Login = lazy(() => import("./Components/auth/Login"));
const Signup = lazy(() => import("./Components/auth/SignUp"));
const EmailVerify = lazy(() => import("./Components/auth/EmailVerify"));
const ForgotPassword = lazy(() => import("./Components/auth/ForgotPassword.jsx"));
const resetPassword = lazy(() => import("./Components/auth/ChangePassword.js"));
const SuccessSignup = lazy(() => import("./Components/auth/SuccessSignup.jsx"));

const HelpCenter = lazy(() => import("./Components/help-center/HelpCenter.jsx"));
const SearchResults = lazy(() => import("./Components/help-center/SearchResults.jsx"));
const ArticleHelpCenter = lazy(() => import("./Components/help-center/articles/ArticleHelpCenter.jsx"));
const SelectedQuestionPage = lazy(() => import("./Components/help-center/articles/ArticleQuestions/SelectedQuestionPage.jsx"));

const DashboardDrawer = lazy(() => import("./Components/NewDashboard/DashboardDrawer"));

const PageNotFound = lazy(() => import("./Components/path404error/patherror.jsx"));
// const DownTime = lazy(() => import("./Components/DownTime.jsx"));
const InitiateMeeting = lazy(
  () => import("./Components/NewDashboard/meetings/InitiateMeeting.jsx"),
);

const stripe = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const App = () => {
  const data = useSelector((state) => (state.auth ? state.auth.data : undefined));
  const dispatch = useDispatch();

  window.addEventListener("storage", (event) => {
    if (event.key === "logout") {
      if (window.location.href.split("/")[3] === "db") {
        window.location.href = "/";
      }
    }
  });

  useEffect(() => {
    AOS.init({
      delay: 20,
      duration: 500,
      easing: "linear",
      offset: 0, // Animation duration in milliseconds
      once: true,
    });

    return () => {
      AOS.refresh(); // Optionally refresh AOS on component unmount
    };
  }, []);

  const location = window.location.href.split("/")[3] === "db";
  useEffect(() => {
    if (location) {
      document.title = "Zodot";
    }
  }, [location]);

  useEffect(() => {
    const socket = initiateSocket(data && data.id);

    const callback = (data) => {
      dispatch(addNotification(data));
    };

    socket.on("notification", callback);
    return () => socket.off("notification", callback);
  }, []);

  useEffect(() => {
    const socket = initiateSocket(data && data.id);

    const callback = async (userPlan) => {
      await dispatch(refreshUserPlan(userPlan));
      await dispatch(setIsPaymentProcessing(false));

      let status;
      switch (userPlan.type) {
        case "canceled":
        case "updated":
        case "confirmed":
          status = userPlan.type;
          window.location.href = `/db/plan?status=${status}`;
          break;

        case "expired":
        case "renewal":
          status = userPlan.type;
          break;

        default:
          status = "";
      }
    };

    socket.on("confirmPayment", callback);
    return () => socket.off("confirmPayment", callback);
  }, []);

  return (
    <BrowserRouter>
      <Switch>
        <Layout>
          <ErrorBoundray>
            <ScrollTop />
            <Suspense fallback={<LazyLoading />}>
            <Switch>
              {/* <Route path="*" exact component={DownTime} /> */}

              <Route path="/" exact component={HomePage} />
              <Route path="/features/invoicing" exact component={Invoicing} />
              <Route path="/features/agreements" exact component={Agreements} />
              <Route
                path="/features/transactions"
                exact
                component={Transactions}
              />
              <Route
                path="/features/client-project-management"
                exact
                component={ClientProjectManagement}
              />
              <Route
                path="/features/time-tracking"
                exact
                component={TimeTracking}
              />
              <Route
                path="/features/task-management"
                exact
                component={TaskManagements}
              />
              <Route path="/features/statements" exact component={Statements} />

              <Route path="/reviews" exact component={Reviews} />
              <Route path="/why-zodot" exact component={WhyZodot} />
              <Route
                path="/terms-of-service"
                exact
                component={TermsOfService}
              />
              <Route path="/privacy-policy" exact component={PrivacyPolicy} />
              <Route path="/contact" exact component={ContactUs} />
              <Route path="/pricing" exact component={PricePage} />
              <Route path="/about" exact component={AboutUs} />
              <Route path="/freelance-workspace" exact component={LandingPage} />


              <ProtectedRoute
                path="/checkout"
                exact
                component={() => (
                  <Elements stripe={stripe}>
                    <Checkout />
                  </Elements>
                )}
              />

              <ProtectedRoute path='/db/plan' exact component={DashboardDrawer} />

              <Route path="/db/signup" exact component={Signup} />
              <Route
                path="/db/signup-success"
                exact
                component={SuccessSignup}
              />
              <Route path="/db/login" exact component={Login} />
              <Route path="/db/forgot" exact component={ForgotPassword} />
              <Route path="/help-center" exact component={HelpCenter} />
              <Route
                path="/help-center/search"
                exact
                component={SearchResults}
              />
              <Route
                path="/help-center/:article"
                exact
                component={ArticleHelpCenter}
              />
              <Route
                path="/help-center/:article/:faq"
                exact
                component={SelectedQuestionPage}
              />
              <Route
                path="/db/verify/:token/:email"
                exact
                component={EmailVerify}
              />
              <Route
                path="/db/reset-password/:resetPasswordToken"
                exact
                component={resetPassword}
              />
              <ProtectedRoute
                path="/db/home"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/tasks&notes"
                exact
                component={LaunchToTasks}
              />
              <ProtectedRoute
                path="/db/tasks&notes/tasks"
                exact
                component={DashboardDrawer}
              />

              <ProtectedRoute
                path="/db/tasks&notes/notes"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/clients"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/clients/:id/tasks"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/clients/:id/address"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/clients/:id/projects"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/clients/:id/invoices"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/clients/:id/transactions"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/clients/:id/notes"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/clients/:id/invoices/create/quickInvoice"
                component={CreateInvoiceInClient}
              />
              <ProtectedRoute
                path="/db/clients/:id/invoices/create/review"
                component={CreateInvoiceInClient}
              />{" "}
              <ProtectedRoute
                path="/db/clients/:id/invoices/create/send"
                component={CreateInvoiceInClient}
              />
              <ProtectedRoute
                path="/db/clients/:id/invoices/:invoiceId/review"
                component={EditInvoiceInClient}
              />{" "}
              <ProtectedRoute
                path="/db/clients/:id/invoices/:invoiceId/send"
                component={EditInvoiceInClient}
              />
              <ProtectedRoute
                path="/db/projects"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/projects/:id/terms"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/projects/:id/tasks"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/projects/:id/timers"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/projects/:id/weekviews"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/projects/:id/reports"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/projects/:id/invoices"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/projects/:id/transactions"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/projects/:id/notes"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/projects/:id/invoices/create/setup"
                component={CreateInvoiceInProject}
              />
              <ProtectedRoute
                path="/db/projects/:id/invoices/create/review"
                component={CreateInvoiceInProject}
              />{" "}
              <ProtectedRoute
                path="/db/projects/:id/invoices/create/send"
                component={CreateInvoiceInProject}
              />
              <ProtectedRoute
                path="/db/projects/:id/invoices/:invoiceId/review"
                component={EditInvoiceInProject}
              />
              <ProtectedRoute
                path="/db/projects/:id/invoices/:invoiceId/send"
                component={EditInvoiceInProject}
              />
              <ProtectedRoute
                path="/db/transactions"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/timetracking"
                exact
                component={LaunchToTimers}
              />
              <ProtectedRoute
                path="/db/timetracking/timers"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/timetracking/weekview"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/timetracking/reports"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/agreements"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/agreements/create/setup"
                exact
                component={CreateAgreements}
              />
              <ProtectedRoute
                path="/db/agreements/create/reviewProposal"
                exact
                component={CreateAgreements}
              />{" "}
              <ProtectedRoute
                path="/db/agreements/create/reviewContract"
                exact
                component={CreateAgreements}
              />{" "}
              <ProtectedRoute
                path="/db/agreements/create/send"
                exact
                component={CreateAgreements}
              />
              <ProtectedRoute
                path="/db/agreements/:id/reviewProposal"
                exact
                component={EditAgreements}
              />
              <ProtectedRoute
                path="/db/agreements/:id/reviewContract"
                exact
                component={EditAgreements}
              />
              <ProtectedRoute
                path="/db/agreements/:id/send"
                exact
                component={EditAgreements}
              />
              <Route
                path="/db/agreements/view/public/:id/:token"
                exact
                component={PublicViewAgreements}
              />
              <ProtectedRoute
                path="/db/agreements/view/:id"
                exact
                component={ViewAgreements}
              />
              <ProtectedRoute
                path="/db/setting"
                exact
                component={LaunchToSetting}
              />
              <ProtectedRoute
                path="/db/setting/business"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/setting/business/business_info"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/setting/business/currency"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/setting/business/brand_logo"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/setting/templates"
                exact
                component={DashboardDrawer}
              />

              <ProtectedRoute
                path="/db/setting/invoice"
                exact
                component={DashboardDrawer}
              />

              <ProtectedRoute
                path="/db/setting/account/"
                exact
                component={LaunchToUserInfo}
              />
              <ProtectedRoute
                path="/db/setting/account/user_info"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/invoices"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/invoices/create"
                exact
                component={CreateInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/create/setup"
                exact
                component={CreateInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/create/setupProject"
                exact
                component={CreateInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/create/setupClient"
                exact
                component={CreateInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/create/quickInvoice"
                exact
                component={CreateInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/create/createClient"
                exact
                component={CreateInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/create/review"
                exact
                component={CreateInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/create/send"
                exact
                component={CreateInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/edit/:id/review"
                exact
                component={EditInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/edit/:id/send"
                exact
                component={EditInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/resend/:id/send"
                exact
                component={ResendInvoice}
              />
              <ProtectedRoute
                path="/db/invoices/view/:id"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/invoices/view/:id/details"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/invoices/view/:id/notes"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/statements"
                exact
                component={LaunchToStatements}
              />
              <ProtectedRoute
                path="/db/statements/incomes"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/statements/expenses"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path="/db/statements/taxes"
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path={`${data && data.role === "admin" && "/db/admin"}`}
                exact
                component={LaunchToAdminDashboard}
              />
              <ProtectedRoute
                path={`${
                  data && data.role === "admin" && "/db/admin/dashboard"
                }`}
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path={`${data && data.role === "admin" && "/db/admin/users"}`}
                exact
                component={DashboardDrawer}
              />
              <ProtectedRoute
                path={`${
                  data && data.role === "admin" && "/db/admin/deletedUsers"
                }`}
                exact
                component={DashboardDrawer}
              />

              <ProtectedRoute path='/db/events' component={DashboardDrawer} />

              <ProtectedRoute path='/db/meetings' component={DashboardDrawer} />

              <ProtectedRoute path='/db/availability' component={DashboardDrawer} />

              <Route path='/:user/:eventId' component={InitiateMeeting} />

              <Route
                path="/db/invoices/view/public/:id"
                exact
                component={InvoicePublic}
              />

              <Route path="*" component={PageNotFound} />
            </Switch>
            </Suspense>
          </ErrorBoundray>
        </Layout>
      </Switch>
    </BrowserRouter>
  );
};
export default App;
